import { datadogRum } from "@datadog/browser-rum";

import Sentry from "@similarweb/sentry-logger";

declare const window: Window & { assetsRoot: string };

const DATADOG_RUM_APP_ID = "31a16d49-5fe9-4345-8abe-ddc4dcef04af";
const DATADOG_RUM_CLIENT_TOKEN = "pub91cfda405c91c54d9e3c5bd2fe79cc6f";
// this is the reporting site. This can be used if we ever choose to proxy the DD requests.
const DATADOG_SITE_NAME = "datadoghq.com";
const DATADOG_SERVICE_NAME = "pro-frontend";
const BUILD_ID_REG_EXP = /[\d]+\.[\d]+\.[\w]+/;

// rum sdk context property names
const APP_ID_PROPERTY_NAME = "application_id";
const SESSION_ID_PROPERTY_NAME = "session_id";
const VIEW_PROPERTY_NAME = "view";

const DATADOG_RUM_ALLOWED_HOSTS_REG_EXP = /^pro\.similarweb\.com$/;

// the percentage of sessions sampled by datadog rum
// i.e. 30% of visitors will be sampled
const DATADOG_RUM_SESSION_SAMPLE_RATE = 30;
// the percentage of sessions (once selected by the above) to include session recordings
// for example: lets say we set a sample rate of 30% and record rate of 20%
// out of 100 sessions 30 will be sampled
// out of the 30 sampled sessions, 20% will be recorded, so 6 sessions will include a recording
const DATADOG_RUM_SESSION_RECORDING_RATE = 20;

// only these URLs will be considered resources for tracing
const DATADOG_RUM_ALLOWED_TRACING_URLS = ["https://test.groupbuyclub.net/api/"];

const contextIncludesProperties = (ctx: object, ...keys: string[]) => {
    return keys.every((key) => key in ctx && (ctx as Record<string, unknown>)[key] !== null);
};

export const isInitialized = (): boolean => {
    const internalContext = datadogRum.getInternalContext();
    if (
        internalContext &&
        contextIncludesProperties(internalContext, APP_ID_PROPERTY_NAME, SESSION_ID_PROPERTY_NAME, VIEW_PROPERTY_NAME)
    ) {
        return true;
    }
    return false;
};

export default function initDatadogRum(): boolean {
    // if sdk is already initialized, this is a noop for idempotency
    if (isInitialized()) {
        Sentry.warning("Datadog RUM SDK init called more than once");
        return true;
    }

    const {
        assetsRoot,
        location: { hostname },
    } = window;

    const isProduction = DATADOG_RUM_ALLOWED_HOSTS_REG_EXP.test(hostname);

    // we want to init datadog rum only for production
    if (!isProduction) {
        return false;
    }

    const [buildId] = assetsRoot.match(BUILD_ID_REG_EXP) ?? [];
    datadogRum.init({
        applicationId: DATADOG_RUM_APP_ID,
        clientToken: DATADOG_RUM_CLIENT_TOKEN,
        site: DATADOG_SITE_NAME,
        service: DATADOG_SERVICE_NAME,
        env: "production",
        version: buildId,
        sessionSampleRate: DATADOG_RUM_SESSION_SAMPLE_RATE,
        sessionReplaySampleRate: DATADOG_RUM_SESSION_RECORDING_RATE,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: "allow",
        trackViewsManually: true, // needed for SPAs, since there are no "physical" page loads
        allowedTracingUrls: DATADOG_RUM_ALLOWED_TRACING_URLS,
        traceSampleRate: DATADOG_RUM_SESSION_SAMPLE_RATE,
        traceContextInjection: "sampled",
    });
    return true;
}
